/**
 * GovernanceObservabilityPage -- TI: SLOs & Incidentes
 * Redesign minimalista v2 — VUKZ logo, grupos separados
 * 100% real data via getOpsSLODashboard, getOpsAlerts, getOpsTelemetry, getGovernanceIncidents
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, XCircle, AlertTriangle, RefreshCw,
  Filter, Copy, ExternalLink
} from 'lucide-react';
import {
  getOpsSLODashboard,
  getOpsAlerts,
  getOpsTelemetry,
  getGovernanceIncidents,
} from '../../services/governance';
import { COLORS } from '../../components/governance/governanceTheme';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const OBS_LABELS = {
  pt: {
    title: 'SLOs & Incidentes',
    subtitle: 'Observabilidade · Alertas · Latencia · Feed de erros dos agentes',
    refresh: 'Atualizar',
    tabSlos: 'SLOs & Alertas',
    tabIncidents: 'Incidentes',
    sloHealth: 'SLO Health',
    meeting: 'Meeting',
    failing: 'Failing',
    total: 'Total',
    sloSection: 'Service Level Objectives',
    noSlo: 'Nenhum SLO disponivel',
    alertsSection: 'Alertas Ativos (24h)',
    noAlerts: 'Nenhum alerta ativo',
    thSev: 'Sev',
    thType: 'Tipo',
    thMessage: 'Mensagem',
    thWhen: 'Quando',
    latencySection: 'Top Endpoints Lentos (6h)',
    noLatency: 'Sem dados de latencia',
    thEndpoint: 'Endpoint',
    thAvg: 'Avg ms',
    thMax: 'Max ms',
    thReqs: 'Reqs',
    allAgents: 'Todos os agentes',
    allSeverities: 'Todas as severidades',
    loadingInc: 'Carregando incidentes...',
    loading: 'Carregando dados...',
    noIncidents: 'Nenhum incidente encontrado',
    noIncidentsSub: 'Todos os agentes operando normalmente.',
    incCount: (n) => `${n} incidente${n !== 1 ? 's' : ''}`,
    copied: 'Copiado',
    copy: 'Copiar',
    openLogs: 'Abrir Logs (KQL)',
    timeRanges: { '1h': 'Última 1h', '24h': 'Últimas 24h', '7d': 'Últimos 7 dias', '30d': 'Últimos 30 dias' },
    measuredAt: 'Medido:',
  },
  en: {
    title: 'SLOs & Incidents',
    subtitle: 'Observability · Alerts · Latency · Agent error feed',
    refresh: 'Refresh',
    tabSlos: 'SLOs & Alerts',
    tabIncidents: 'Incidents',
    sloHealth: 'SLO Health',
    meeting: 'Meeting',
    failing: 'Failing',
    total: 'Total',
    sloSection: 'Service Level Objectives',
    noSlo: 'No SLOs available',
    alertsSection: 'Active Alerts (24h)',
    noAlerts: 'No active alerts',
    thSev: 'Sev',
    thType: 'Type',
    thMessage: 'Message',
    thWhen: 'When',
    latencySection: 'Top Slow Endpoints (6h)',
    noLatency: 'No latency data',
    thEndpoint: 'Endpoint',
    thAvg: 'Avg ms',
    thMax: 'Max ms',
    thReqs: 'Reqs',
    allAgents: 'All agents',
    allSeverities: 'All severities',
    loadingInc: 'Loading incidents...',
    loading: 'Loading data...',
    noIncidents: 'No incidents found',
    noIncidentsSub: 'All agents operating normally.',
    incCount: (n) => `${n} incident${n !== 1 ? 's' : ''}`,
    copied: 'Copied',
    copy: 'Copy',
    openLogs: 'Open Logs (KQL)',
    timeRanges: { '1h': 'Last 1h', '24h': 'Last 24h', '7d': 'Last 7 days', '30d': 'Last 30 days' },
    measuredAt: 'Measured:',
  },
};

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '\u2014';

// ─── Design Primitives ───────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-4">{children}</p>
);

const Divider = () => <div className="border-t border-gray-100 my-6" />;

const SevBadge = ({ severity }) => {
  const map = {
    critical: 'bg-rose-100 text-rose-700',
    high: 'bg-amber-100 text-amber-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-blue-100 text-blue-700',
  };
  const dot = {
    critical: 'bg-rose-500',
    high: 'bg-amber-500',
    medium: 'bg-yellow-400',
    low: 'bg-blue-400',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${map[severity] || 'bg-gray-100 text-gray-600'}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot[severity] || 'bg-gray-400'}`} />
      {severity}
    </span>
  );
};

const TabBar = ({ tabs, active, onChange }) => (
  <div className="flex gap-0.5 bg-gray-100 p-0.5 rounded-xl w-fit">
    {tabs.map(t => (
      <button key={t.id} onClick={() => onChange(t.id)}
        className={`px-5 py-2 text-xs font-bold rounded-xl transition-all ${active === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
        {t.label}
        {t.badge != null && (
          <span className={`ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-extrabold ${t.badgeColor === 'red' ? 'bg-rose-100 text-rose-700' : t.badgeColor === 'amber' ? 'bg-amber-100 text-amber-700' : 'bg-gray-200 text-gray-600'}`}>
            {t.badge}
          </span>
        )}
      </button>
    ))}
  </div>
);

// ─── SLO Card ────────────────────────────────────────────────────────────────
const SLOCard = ({ slo }) => {
  const meeting = slo.status === 'MEETING_SLO';
  const pct = slo.target_value > 0 ? Math.min(100, Math.max(0, (slo.current_value / slo.target_value) * 100)) : 0;
  return (
    <div className={`bg-white rounded-xl border p-5 ${meeting ? 'border-green-100' : 'border-rose-100'}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <span className="text-xs font-semibold text-gray-600 leading-tight">{slo.slo_name}</span>
        {meeting ? <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-rose-500 flex-shrink-0" />}
      </div>
      <div className={`text-3xl font-black mb-1 ${meeting ? 'text-green-700' : 'text-rose-700'}`}>
        {slo.current_value != null ? slo.current_value.toFixed(2) : '\u2014'}{slo.unit}
      </div>
      <div className="flex items-center justify-between text-[10px] text-gray-400 mb-3">
        <span>Target: {slo.target_value}{slo.unit}</span>
        <span className={`font-bold ${meeting ? 'text-green-600' : 'text-rose-600'}`}>
          {meeting ? '\u2713 MEETING' : '\u2717 FAILING'}
        </span>
      </div>
      <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${meeting ? 'bg-green-400' : 'bg-rose-400'}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

// ─── SLOs Tab ────────────────────────────────────────────────────────────────
const SLOsTab = ({ sloData, alerts, latency, t }) => {
  const slos = sloData?.slos ? Object.values(sloData.slos) : [];
  const summary = sloData?.summary || {};
  const alertsList = alerts?.alerts || [];
  return (
    <div>
      {/* Health Banner */}
      <div className="bg-white rounded-xl border border-gray-100 px-6 py-5 mb-6">
        <div className="flex flex-wrap items-center gap-8">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">{t.sloHealth}</p>
            <div className="text-5xl font-black" style={{ color: COLORS.primary }}>
              {summary.health_percentage?.toFixed(0) ?? '\u2014'}%
            </div>
          </div>
          <div className="h-12 w-px bg-gray-100" />
          <div className="flex gap-8">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">{t.meeting}</p>
              <div className="text-2xl font-black text-green-700">{summary.meeting_slos ?? 0}</div>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">{t.failing}</p>
              <div className="text-2xl font-black text-rose-700">{summary.failing_slos ?? 0}</div>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">{t.total}</p>
              <div className="text-2xl font-black text-gray-400">{summary.total_slos ?? 0}</div>
            </div>
          </div>
          {summary.measured_at && (
            <p className="ml-auto text-[10px] text-gray-400">
              {t.measuredAt} {fmtDate(summary.measured_at)}
            </p>
          )}
        </div>
      </div>

      {/* SLOs Grid */}
      <SectionLabel>{t.sloSection}</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {slos.map((slo, idx) => <SLOCard key={idx} slo={slo} />)}
        {slos.length === 0 && (
          <div className="col-span-3 bg-gray-50 rounded-xl p-8 text-center text-xs text-gray-400">{t.noSlo}</div>
        )}
      </div>

      <Divider />

      {/* Alerts */}
      <div className="flex items-center justify-between mb-4">
        <SectionLabel>{t.alertsSection}</SectionLabel>
        {alerts?.summary && (
          <div className="flex gap-2 -mt-4">
            {(alerts.summary.critical ?? 0) > 0 && (
              <span className="bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full text-[10px] font-bold">{alerts.summary.critical} critical</span>
            )}
            {(alerts.summary.high ?? 0) > 0 && (
              <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-[10px] font-bold">{alerts.summary.high} high</span>
            )}
            <span className="bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full text-[10px] font-bold">{alerts.summary.total ?? 0} total</span>
          </div>
        )}
      </div>

      {alertsList.length === 0 ? (
        <div className="bg-green-50 border border-green-100 rounded-xl p-5 flex items-center gap-3 mb-6">
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
          <span className="text-sm font-semibold text-green-700">{t.noAlerts}</span>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden mb-6">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-50">
                <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-gray-400">{t.thSev}</th>
                <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-gray-400">{t.thType}</th>
                <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-gray-400">{t.thMessage}</th>
                <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-gray-400">{t.thWhen}</th>
              </tr>
            </thead>
            <tbody>
              {alertsList.map((a, idx) => (
                <tr key={a.alert_id || idx} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                  <td className="px-5 py-3"><SevBadge severity={a.severity} /></td>
                  <td className="px-5 py-3 font-mono text-[10px] text-gray-500">{a.alert_type}</td>
                  <td className="px-5 py-3 text-xs text-gray-600 max-w-xs truncate">{a.message}</td>
                  <td className="px-5 py-3 text-[10px] text-gray-400">{fmtDate(a.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Divider />

      {/* Latency */}
      <SectionLabel>{t.latencySection}</SectionLabel>
      {(!latency?.endpoints || latency.endpoints.length === 0) ? (
        <div className="bg-gray-50 rounded-xl p-6 text-center text-xs text-gray-400">{t.noLatency}</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-50">
                <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-gray-400">{t.thEndpoint}</th>
                <th className="px-5 py-3 text-right text-[10px] font-bold uppercase tracking-widest text-gray-400">{t.thAvg}</th>
                <th className="px-5 py-3 text-right text-[10px] font-bold uppercase tracking-widest text-gray-400">{t.thMax}</th>
                <th className="px-5 py-3 text-right text-[10px] font-bold uppercase tracking-widest text-gray-400">{t.thReqs}</th>
              </tr>
            </thead>
            <tbody>
              {latency.endpoints.map((ep, idx) => (
                <tr key={idx} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                  <td className="px-5 py-3 font-mono text-xs text-gray-700">{ep.endpoint}</td>
                  <td className={`px-5 py-3 text-right text-xs font-bold ${ep.avg_latency_ms > 2000 ? 'text-rose-600' : ep.avg_latency_ms > 1000 ? 'text-amber-600' : 'text-gray-700'}`}>
                    {ep.avg_latency_ms?.toFixed(0)}
                  </td>
                  <td className="px-5 py-3 text-right text-xs text-gray-400">{ep.max_latency_ms?.toFixed(0)}</td>
                  <td className="px-5 py-3 text-right text-xs text-gray-400">{ep.request_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Incidents Tab ───────────────────────────────────────────────────────────
const IncidentsTab = ({ incidents, loading, agentFilter, setAgentFilter, severityFilter, setSeverityFilter, timeRange, setTimeRange, t }) => {
  const [copied, setCopied] = useState(null);
  const uniqueAgents = [...new Set(incidents.map(i => i.agent_name))];
  const copy = (text, id) => { navigator.clipboard.writeText(text); setCopied(id); setTimeout(() => setCopied(null), 2000); };
  return (
    <div>
      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5 flex flex-wrap items-center gap-3">
        <Filter size={14} className="text-gray-400 flex-shrink-0" />
        <select value={agentFilter} onChange={e => setAgentFilter(e.target.value)}
          className="text-xs px-3 py-1.5 border border-gray-100 rounded-lg bg-white text-gray-700 focus:outline-none">
          <option value="all">{t.allAgents}</option>
          {uniqueAgents.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={severityFilter} onChange={e => setSeverityFilter(e.target.value)}
          className="text-xs px-3 py-1.5 border border-gray-100 rounded-lg bg-white text-gray-700 focus:outline-none">
          <option value="all">{t.allSeverities}</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select value={timeRange} onChange={e => setTimeRange(e.target.value)}
          className="text-xs px-3 py-1.5 border border-gray-100 rounded-lg bg-white text-gray-700 focus:outline-none">
          {Object.entries(t.timeRanges).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <span className="text-[10px] text-gray-400 ml-auto">{t.incCount(incidents.length)}</span>
      </div>

      {loading && (
        <div className="bg-white rounded-xl border border-gray-100 p-10 text-center">
          <RefreshCw size={24} className="animate-spin text-gray-200 mx-auto mb-2" />
          <p className="text-xs text-gray-400">{t.loadingInc}</p>
        </div>
      )}

      {!loading && incidents.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-10 text-center">
          <CheckCircle size={36} className="text-green-400 mx-auto mb-3" />
          <p className="text-sm font-bold text-gray-700 mb-1">{t.noIncidents}</p>
          <p className="text-xs text-gray-400">{t.noIncidentsSub}</p>
        </div>
      )}

      {!loading && incidents.map((incident, idx) => (
        <div key={idx} className="bg-white rounded-xl border border-gray-100 p-5 mb-3 hover:border-gray-200 transition-colors">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className="text-xs font-bold text-gray-900">{incident.agent_name}</span>
                <SevBadge severity={incident.severity} />
              </div>
              <p className="text-xs text-gray-500 leading-relaxed mb-2">{incident.error_message}</p>
              <div className="flex gap-4 text-[10px] text-gray-400">
                <span>{fmtDate(incident.timestamp)}</span>
                {incident.duration_ms && <span>{incident.duration_ms}ms</span>}
              </div>
            </div>
            <AlertTriangle size={16} className="text-rose-300 flex-shrink-0 mt-0.5" />
          </div>
          {incident.run_id && (
            <div className="bg-gray-50 rounded-lg px-3 py-2 flex items-center justify-between gap-2 mt-3">
              <code className="text-[10px] text-gray-500 font-mono truncate">{incident.run_id}</code>
              <button onClick={() => copy(incident.run_id, idx)}
                className="flex-shrink-0 flex items-center gap-1 px-2 py-1 text-[10px] bg-white border border-gray-100 rounded hover:bg-gray-50">
                {copied === idx
                  ? <><CheckCircle size={10} className="text-green-500" /> {t.copied}</>
                  : <><Copy size={10} /> {t.copy}</>
                }
              </button>
            </div>
          )}
          {incident.kql_link && (
            <a href={incident.kql_link} target="_blank" rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold text-white rounded-lg"
              style={{ backgroundColor: COLORS.primary }}>
              <ExternalLink size={11} /> {t.openLogs}
            </a>
          )}
        </div>
      ))}
    </div>
  );
};

const GovernanceObservabilityPage = () => {
  const [tab, setTab] = useState('slos');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sloData, setSloData] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [latency, setLatency] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [incLoading, setIncLoading] = useState(false);
  const [agentFilter, setAgentFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [timeRange, setTimeRange] = useState('24h');

  const { lang } = useGovernanceLang();
  const t = OBS_LABELS[lang] || OBS_LABELS.pt;

  const fetchSLOs = useCallback(async () => {
    const [slo, alertsData, telemetry] = await Promise.all([
      getOpsSLODashboard().catch(() => null),
      getOpsAlerts({ time_window: 'last_24h', limit: 20 }).catch(() => null),
      getOpsTelemetry({ time_window: 'last_6h', limit: 10 }).catch(() => null),
    ]);
    setSloData(slo);
    setAlerts(alertsData);
    setLatency(telemetry);
  }, []);

  const fetchIncidents = useCallback(async () => {
    setIncLoading(true);
    try {
      const res = await getGovernanceIncidents({
        agent: agentFilter === 'all' ? undefined : agentFilter,
        severity: severityFilter === 'all' ? undefined : severityFilter,
        time_range: timeRange,
      });
      setIncidents(res.incidents || []);
    } catch (err) {
      console.error('[Incidents]', err);
    } finally {
      setIncLoading(false);
    }
  }, [agentFilter, severityFilter, timeRange]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchSLOs(), fetchIncidents()]);
      setLoading(false);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loading) fetchIncidents();
  }, [agentFilter, severityFilter, timeRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchSLOs(), fetchIncidents()]);
    setRefreshing(false);
  };

  const failingSLOs = sloData?.summary?.failing_slos ?? 0;
  const totalAlerts = alerts?.summary?.total ?? 0;
  const criticalIncidents = incidents.filter(i => i.severity === 'critical').length;

  const tabs = [
    {
      id: 'slos',
      label: t.tabSlos,
      badge: failingSLOs > 0 ? failingSLOs : (totalAlerts > 0 ? totalAlerts : null),
      badgeColor: failingSLOs > 0 ? 'red' : 'amber',
    },
    {
      id: 'incidents',
      label: t.tabIncidents,
      badge: incidents.length > 0 ? incidents.length : null,
      badgeColor: criticalIncidents > 0 ? 'red' : 'amber',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Masthead ────────────────────────────────────────────────────── */}
      <div className="bg-gray-950 px-8 py-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img
            src="/vukz-logo.png"
            alt="Vukz"
            className="w-10 h-10 rounded-xl object-contain"
            onError={e => { e.target.style.display = 'none'; }}
          />
          <div>
            <h1 className="text-white text-xl font-black tracking-tight">{t.title}</h1>
            <p className="text-gray-400 text-xs mt-0.5">{t.subtitle}</p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading || refreshing}
          className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-gray-300 border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-40"
        >
          <RefreshCw size={14} className={(loading || refreshing) ? 'animate-spin' : ''} />
          {t.refresh}
        </button>
      </div>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <div className="px-8 py-8 max-w-6xl">
        <div className="mb-6">
          <TabBar tabs={tabs} active={tab} onChange={setTab} />
        </div>

        {loading ? (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
            <RefreshCw size={28} className="animate-spin text-gray-200 mx-auto mb-3" />
            <p className="text-xs text-gray-400">{t.loading}</p>
          </div>
        ) : (
          <>
            {tab === 'slos' && <SLOsTab sloData={sloData} alerts={alerts} latency={latency} t={t} />}
            {tab === 'incidents' && (
              <IncidentsTab
                incidents={incidents}
                loading={incLoading}
                agentFilter={agentFilter}
                setAgentFilter={setAgentFilter}
                severityFilter={severityFilter}
                setSeverityFilter={setSeverityFilter}
                timeRange={timeRange}
                setTimeRange={setTimeRange}
                t={t}
              />
            )}
          </>
        )}
      </div>

    </div>
  );
};

export default GovernanceObservabilityPage;
